import { useEffect, useRef, useState } from 'react';

const DEFAULT_BATCH_SIZE = 24;

/** Start rendering the next batch this far before the sentinel reaches the viewport. */
const PRELOAD_MARGIN = '600px 0px';

/**
 * Renders a long list in batches: the first `batchSize` items up front, then the next batch each
 * time the sentinel element (placed after the list) comes near the viewport.
 *
 * Rendering hundreds of meal cards at once — each with a cover image and a Lottie icon — exhausts
 * the memory of iOS Safari, which then kills the page.
 *
 * @param items the full list
 * @param batchSize how many items to add per batch
 * @returns the items to render, a ref for the sentinel and whether any items are still hidden
 */
const useIncrementalRender = <T>(items: T[], batchSize = DEFAULT_BATCH_SIZE) => {
  const [visibleCount, setVisibleCount] = useState(batchSize);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const hasMore = visibleCount < items.length;

  // Observed anew after every batch (visibleCount in deps): the browser reports an intersection
  // only when it changes, so a sentinel still in view after a batch would never fire again.
  useEffect(() => {
    if (!hasMore) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setVisibleCount(items.length);
      return;
    }

    const sentinel = sentinelRef.current;

    if (!sentinel) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleCount((count) => count + batchSize);
        }
      },
      { rootMargin: PRELOAD_MARGIN }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [hasMore, visibleCount, items.length, batchSize]);

  return {
    visibleItems: items.slice(0, visibleCount),
    sentinelRef,
    hasMore,
  };
};

export default useIncrementalRender;
